import i18next from 'i18next';
import en from '../i18n/en-us.json';
import pt from '../i18n/pt-br.json';
import es from '../i18n/es-es.json';
import { Modal } from "react-bootstrap"
import { Box, Chip, FormControl, MenuItem, Select, SelectChangeEvent, TextField } from "@mui/material";
import { Cancel } from "@mui/icons-material";
import { ChangeEvent, useEffect, useState } from "react";
import { DepartmentModel, NotificationModel, NotificationPostViewModel, NotificationType } from '../model/notification.model';
import { NotificationService as Service } from '../services/notification.service';
import { useTranslation } from 'react-i18next';
import { AlertTypeIcon } from 'common/helpers/alerts/alert-type-icon';
import { toast } from 'react-toastify';
import ProtectedComponent from 'common/partials/ProtectedComponent';
import { RolesDictionary } from 'common/helpers/dictionaries/roles-dictionary';
import { useNavigate } from 'react-router-dom';
import { DeleteModal } from 'common/partials/DeleteModal';
import { BaseHttpResponse } from 'common/interfaces/base-http-response.model';
import SaveButton from 'common/partials/SaveButton';
import closeModalIcon from 'assets/images/icons/close.svg';
import ITSVG from 'common/helpers/ITSVG';

interface NotificationAddProps {
    notification?: NotificationModel;
    isOpen: boolean;
    close: () => void;
}

export function NotificationAdd(props: NotificationAddProps) {
    i18next.addResourceBundle('us', 'translation', en);
    i18next.addResourceBundle('br', 'translation', pt);
    i18next.addResourceBundle('es', 'translation', es);

    const { t } = useTranslation();
    const navigate = useNavigate();
    const [departaments, setDepartaments] = useState<DepartmentModel[]>([]);
    const [toDepartments, setToDepartments] = useState<string[]>(props.notification ? props.notification.departments.map(x => x.id.toString()) : []);
    const [message, setMessage] = useState(props.notification ? props.notification.content : "");
    const [title, setTitle] = useState(props.notification ? props.notification.title : "");
    const [pinned, setPinned] = useState(props.notification ? props.notification.pinned : false);
    const [type, setType] = useState<NotificationType>(props.notification ? props.notification.type : NotificationType.Info);
    const [loading, setLoading] = useState(false);
    const [deleteModal, setDeleteModal] = useState(false);

    const notifications = [NotificationType.Success, NotificationType.Error, NotificationType.Info, NotificationType.Warning];

    const fetchData = async () => {
        try {
            const response = await Service.getDepartments();
            if (response) {
                if (!response.hasErrors) {
                    setDepartaments(response.data);
                }
            }
        } catch (error) {
            toast.error(t("notifications.errors.noResponse"))
        }
    }

    const deleteDepartment = (value?: DepartmentModel) => {
        if (!value) return;
        const newDepartments = departaments.filter(d => d.id != value.id);
        setToDepartments(newDepartments.map(x => x.id.toString()));
    };

    const departmentsHandleChange = (event: SelectChangeEvent<string[]>) => {
        const { target: { value } } = event;
        const selected = typeof value === 'string' ? value.split(',') : value.map(x => x.toString());
        if (selected.indexOf("-1") > -1) {
            setToDepartments(departaments.map((x: DepartmentModel) => x.id.toString()));
        } else {
            setToDepartments(selected);
        }
    };

    const disabledLoadingOrEdit = function () {
        if (loading) return true;
        if (props.notification !== undefined) return true;
        return false;
    }

    const saveData = async () => {
        if (toDepartments.length === 0) {
            toast.error(`${t("notifications.errors.departments")}`);
            return;
        }
        if (title.length === 0) {
            toast.error(`${t("notifications.errors.title")}`);
            return;
        }
        if (message.length === 0) {
            toast.error(`${t("notifications.errors.message")}`);
            return;
        }

        const request: NotificationPostViewModel = {
            content: message,
            notificationContentType: 'Text',
            notificationType: type,
            pinned: pinned,
            rawContent: message,
            title: title,
            toDepartmentIds: toDepartments.map(x => parseInt(x)),
            toUserIds: []
        };

        setLoading(true);
        if (props.notification) {
            const response = await Service.putNotification(props.notification.id, request);
            handleResult(response);
        } else {
            const response = await Service.postNotification(request);
            handleResult(response);
        }
        setLoading(false);
        props.close();
    }

    const handleResult = (response: BaseHttpResponse<unknown> | undefined) => {
        if (response && !response.hasErrors) {
            toast.success(t("notifications.add.success"));
        } else {
            toast.success(t("notifications.errors.noResponse"));
        }
    }

    const handleDelete = async () => {
        try {
            if (props.notification) {
                setLoading(true);
                await Service.deleteNotification(props.notification);
                setDeleteModal(false);
                setLoading(false);
                props.close();
            }
        } catch (error) {
            toast.error(`${t("notifications.errors.noResponse")}`);
        }
    }

    useEffect(() => {
        fetchData();

        if (props.notification) {
            Service.markAsRead(props.notification);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props]);
    return (
        <Modal
            show={props.isOpen}
            onHide={props.close}
            dialogClassName="modal-dialog-centered mw-900px h-auto"
        >
            <div className="modal-content">
                <div className={`modal-header d-flex justify-content-between align-items-center`}>
                    <h3 className="modal-title">
                        {props.notification
                            ? props.notification.title
                            : t("notifications.add.title")}
                    </h3>
                    <div className="btn btn-sm btn-icon btn-active-color-primary" onClick={props.close}>
                        <ITSVG path={closeModalIcon} className="svg-icon-1" />
                    </div>
                </div>

                <form onSubmit={saveData}>
                    <div className="modal-body scroll-y mx-1 mx-xl-1 my-1">
                        <DeleteModal
                            open={deleteModal}
                            handleDelete={handleDelete}
                            onClose={() => setDeleteModal(false)}
                            size="sm"
                            text=""
                            type="delete"
                        />

                        {props.notification == null &&
                            <ProtectedComponent action="hide" role={[RolesDictionary.Notifications.Notification_Create]}>
                                <div className="mb-10">
                                    <label className="form-label fw-bold">{t("notifications.add.department.title")}</label>
                                    <FormControl variant="filled" fullWidth disabled={loading}>
                                        <Select
                                            multiple
                                            required
                                            name="department"
                                            value={toDepartments}
                                            onChange={departmentsHandleChange}
                                            disabled={disabledLoadingOrEdit()}
                                            renderValue={(selected: string[]) => (
                                                <Box sx={{ display: "flex", flexWrap: "nowrap", gap: 0.5, overflow: "auto" }}>
                                                    {selected.map((value) => (
                                                        <Chip
                                                            key={value}
                                                            label={departaments.find(x => x.id.toString() === value)?.name}
                                                            clickable={true}
                                                            disabled={disabledLoadingOrEdit()}
                                                            deleteIcon={<Cancel onMouseDown={(e) => e.stopPropagation()} />}
                                                            onDelete={() => deleteDepartment(departaments.find(d => d.id.toString() === value))}
                                                        />
                                                    ))}
                                                </Box>
                                            )}
                                        >
                                            <MenuItem value="-1">{t("notifications.add.department.all")}</MenuItem>
                                            {departaments.map(dept => (
                                                <MenuItem key={dept.id} value={dept.id.toString()}>
                                                    {dept.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </div>
                            </ProtectedComponent>
                        }

                        {props.notification == null &&
                            <div className="mb-10">
                                <label className="form-label fw-bold">{t("notifications.add.subject")}</label>
                                <TextField
                                    className="form-control form-control-lg form-control-solid"
                                    onChange={(e) => setTitle(e.target.value)}
                                    onBlur={(e) => setTitle(e.target.value)}
                                    value={title}
                                    disabled={disabledLoadingOrEdit()}
                                    fullWidth
                                />
                            </div>
                        }

                        <div className="mb-10">
                            <label className="form-label fw-bold">{t("notifications.add.placeholder")}</label>
                            <TextField
                                className="form-control form-control-lg form-control-solid"
                                style={{ backgroundColor: "light-dark", color: 'rgb(94, 98, 120)' }}
                                rows={5}
                                onChange={(e) => setMessage(e.target.value)}
                                onBlur={(e) => setMessage(e.target.value)}
                                value={message}
                                disabled={disabledLoadingOrEdit()}
                                multiline
                                fullWidth
                            />
                        </div>

                        <div className="d-flex align-items-center gap-4">
                            {props.notification && (
                                <button
                                    type="button"
                                    className="btn btn-icon btn-light-danger"
                                    onClick={() => setDeleteModal(true)}
                                >
                                    <i className="fa-solid fa-trash-can"></i>
                                </button>
                            )}

                            <button
                                type="button"
                                disabled={disabledLoadingOrEdit()}
                                className={`btn btn-icon ${pinned ? 'btn-light-primary' : 'btn-light'}`}
                                onClick={() => setPinned(!pinned)}
                            >
                                <i className="fa-solid fa-thumbtack"></i>
                            </button>

                            <div className="dropdown">
                                <button
                                    type="button"
                                    className={`btn btn-icon btn-light`}
                                    data-bs-toggle="dropdown"
                                    aria-expanded="false"
                                    disabled={disabledLoadingOrEdit()}
                                    title={t("notifications.add.options.title")}
                                >
                                    <AlertTypeIcon notificationType={type} isOn={false} border={false} />
                                </button>
                                <div className="dropdown-menu" >
                                    {notifications.map((n, i) => (
                                        <div className={`form-check ps-2 ${i != notifications.length - 1 ? 'pb-1' : ''}`} key={n}>
                                            <input
                                                className="form-check-input d-none"
                                                type="radio"
                                                name="type"
                                                id={n.toString()}
                                                checked={type === n}
                                                onChange={() => setType(n)}
                                            />
                                            <label className="form-check-label d-flex align-items-center" htmlFor={n.toString()}>
                                                <span className="symbol symbol-35px me-2">
                                                    <AlertTypeIcon notificationType={n} isOn={type == n} />
                                                </span>
                                                {t(`notifications.add.options.types.${NotificationType[n].toLowerCase()}`)}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="modal-footer d-flex justify-content-end py-6 px-9">
                        <button type="button" className="btn btn-light btn-active-light-primary me-2" onClick={props.close}>
                            {t('crud.update.buttonCancel')}
                        </button>
                        <SaveButton type="submit" loading={loading} disabled={disabledLoadingOrEdit()} />
                    </div>
                </form >
            </div >
        </Modal >
    );
}
